import { cloneElement, useState } from 'react'
import { Transition } from '@headlessui/react'
import Translation from '../../../../Translation'
import { classNames } from '@progress/kendo-react-common'
import { useAppContext } from '../../../../../context/AppContext'
import { DropDownList } from '@progress/kendo-react-dropdowns'
import { useEffect } from 'react'
import { TimePicker } from '@progress/kendo-react-dateinputs'
import { days_of_week } from '../../../Class/ClassItem'
import { ReactElement } from 'react'
import ConfirmDialogModal from '../../../ConfirmDialog'
import { getTranslation } from '../../../../../utils/language-helper'
import CustomSelectServices from './CustomServiceSelector'
import { useTenantContext } from '../../../../../context/TenantContext'
import ServiceAvailSelector from '../../../../DropdownSelectors/ServiceAvailabilitySelector'

export type CustomFormProps = {
    show: boolean
    data: any
    services: any
    locations: any
    translations: any
    timezone: any
    onSave(staff_dto: any): void
    onDelete?(id: string): void
}

export const FormWithCustomEditor = (p: CustomFormProps) => {
    const { translations } = useAppContext()
    const { locations } = useTenantContext()
    const [availability, setAvailability] = useState<boolean>(false)
    const [show_modal, setShowModal] = useState<boolean>(false)
    const [disabled, setDisabled] = useState<boolean>(true)
    const [show_delete, setShowDelete] = useState<boolean>(false)
    const [showConfirmation, setShowConfirmation] = useState(false)
    const [current_location, setCurrentLocation] = useState<string>(p.locations[0]?.value)
    const [day_of_weeks, setDayOfWeeks] = useState<any>([days_of_week[
        new Date(p.data.dataItem.start).getDay()
    ]])
    const [staff_dto, setStaffDto] = useState<any>()

    const [attributes, setAttributes] = useState<any>(p)

    const day_options: ReactElement[] = []
    const [space_items, setSpaceItems] = useState<Record<string, string>[]>([])
    const [selected_spaces, setSelectedSpaces] = useState<
        string[]
    >([])

    useEffect(() => {
        const location = locations.filter((loc: any) => loc?.id === current_location)[0]
        if (location && location.location_spaces) {
            const spaces_cache = location.location_spaces
                .map((loc_space: any) => {
                    return ({
                        id: loc_space.id,
                        name: loc_space.name
                    })
                })
            setSpaceItems(spaces_cache)

            const loc_spaces: any[] = []
            if (p.data.dataItem.id && p.data.dataItem?.locationSpaceIds) {
                p.data.dataItem.locationSpaceIds.map((loc_space_id: string) => {
                    const selected_space = spaces_cache.filter((space: any) => space?.id === loc_space_id)[0]
                    if (selected_space) {
                        loc_spaces.push(selected_space)
                    }
                })
            }
            setSelectedSpaces(loc_spaces)
        }
    }, [locations, current_location])

    useEffect(() => {
        setCurrentLocation(staff_dto?.location?.value)
        setStaffDto({
            ...staff_dto,
            block: availability,
            day_of_weeks: day_of_weeks,
            categoryIds: attributes?.selected_categories,
            serviceIds: attributes?.selected_services,
            from_time: p.data.dataItem.start,
            to_time: p.data.dataItem.end,
            data_id: p.data.dataItem.id,
        })
        setDisabled(false)
    }, [attributes?.selected_categories, attributes?.selected_services, staff_dto?.location, staff_dto?.location_space, availability])

    useEffect(() => {
        const services = staff_dto?.serviceIds && staff_dto?.serviceIds?.length != 0
        const categories = staff_dto?.categoryIds && staff_dto?.categoryIds?.length != 0
        setDisabled(!(services || categories))
    }, [staff_dto?.serviceIds, staff_dto?.categoryIds])

    useEffect(() => {
        setShowModal(p.show)
        setStaffDto({
            ...staff_dto,
            location: p.locations[0],
        })

        if (p.data.dataItem.id) {
            setShowDelete(true)
            setAvailability(p.data.dataItem.block)


            const location = {
                value: p.data.dataItem?.location?.id,
                text: p.data.dataItem?.location?.name
            }


            const service_ids = p.data.dataItem?.services?.map((item: any) => item.serviceId)
            const category_ids = p.data.dataItem?.categories?.map((item: any) => item.categoryId)
            setDayOfWeeks(p.data.dataItem.dayOfWeeks)

            setAttributes({
                ...attributes,
                selected_services: service_ids,
                selected_categories: category_ids
            })

            setCurrentLocation(staff_dto?.location?.value)
            setStaffDto({
                ...staff_dto,
                location: location,
                data_id: p.data.dataItem.id
            })
        }
    }, [p.show])

    function updateLocationSpace(location: any) {
        const spaces_cache = locations?.filter((loc: any) => loc?.id === location.value)?.[0]?.location_spaces
            ?.map((loc_space: any) => {
                return ({
                    id: loc_space.id,
                    name: loc_space.name
                })
            }) || []
        const mapped = spaces_cache?.map((item: any) => item.id as string) || []
        setSpaceItems(spaces_cache)
        setSelectedSpaces(mapped)
        setStaffDto({
            ...staff_dto,
            location_space: mapped,
            location: location,
        })
    }

    function handleSubmitForm() {
        if (staff_dto?.location_space && staff_dto?.location_space.length == space_items.length) {
            delete staff_dto?.location_space
        }
        p.onSave(staff_dto)
        setShowModal(false)
    }

    function renderDays() {
        for (let i = 0; i < days_of_week.length; i++) {
            day_options.push(
                <button
                    type="button"
                    className={classNames(day_of_weeks.includes(days_of_week[i]) ? 'bg-cyan-800 text-white font-bold' : 'bg-gray-300 text-black',
                        'border rounded-md py-1 px-2 text-center items-center'
                    )}
                    onClick={(e) => {
                        if (day_of_weeks.includes(days_of_week[i])) {
                            day_of_weeks.splice(day_of_weeks.indexOf(days_of_week[i]), 1)
                        } else {
                            day_of_weeks.push(days_of_week[i])
                        }
                        setStaffDto({
                            ...staff_dto,
                            day_of_weeks: day_of_weeks
                        })
                    }}
                    data-value={p.data.dataItem.start}
                    key={days_of_week[i]}
                >
                    <span
                        className='block uppercase text-md tracking-widest font-display font-semibold'
                    >
                        {days_of_week[i].substr(0, 3)}
                    </span>
                </button>
            )
        }
        return day_options
    }

    function RenderAvailabilityTabs() {
        return (
            <div className="flex items-center tracking-wide py-3">
                <button
                    type="button"
                    className={classNames(
                        !availability
                            ? 'bg-emerald-500/20 text-emerald-500'
                            : 'bg-gray-100 text-gray-600/60',
                        'rounded-l-lg py-2 w-1/2'
                    )}
                    onClick={() => {
                        setAvailability(false)
                        setStaffDto({
                            ...staff_dto,
                            block: availability,
                        })
                    }}
                >
                    <Translation
                        className="align-middle text-primary"
                        content_key="Available"
                        translations={translations}
                    />
                </button>
                <button
                    type="button"
                    className={classNames(
                        availability
                            ? 'bg-red-600/20 text-red-600'
                            : 'bg-gray-100 text-gray-600/60',
                        'rounded-r-lg py-2 w-1/2'
                    )}
                    onClick={() => {
                        setAvailability(true)
                        setStaffDto({
                            ...staff_dto,
                            block: availability,
                        })
                    }}
                >
                    <Translation
                        className="font-bold align-middle"
                        content_key="Block"
                        translations={translations}
                    />
                </button>
            </div>
        )
    }

    function LocationValueRender(
        element: React.ReactElement<HTMLSpanElement>,
        value: any
    ) {
        const children = [
            <div key={1}>
                <i className=" px-2 feather-map-pin" />
                {value ? value.name : 'Location'}
            </div>,
        ]

        return cloneElement(element, { ...element.props }, children)
    }

    return (
        <div>
            <Transition
                show={show_modal}
                enter="transition-opacity duration-200"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-all duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                as="div"
                className="absolute inset-0 top-0 z-30 flex justify-center"
            >
                <Transition.Child
                    enter="transition-all duration-600"
                    enterFrom="-translate-y-8"
                    enterTo="translate-y-0"
                    leave="transition-all duration-300"
                    leaveFrom="translate-y-0 opacity-30"
                    leaveTo="-translate-y-64 opacity-0"
                    className="border-gray-800 drop-shadow-2xl"
                >
                    <div
                        className='z-30 p-5 bg-white w-auto rounded-xl mx-auto border-gray-300'
                    >
                        <Translation
                            render_as="h3"
                            content_key={p.data.dataItem.id ? "edit_availability" : "add_availability"}
                            className="text-primary font-semibold text-lg"
                        />
                        <div className="flex items-center justify-center gap-3 pt-4">
                            {renderDays()}
                        </div>
                        <div className="flex gap-1 items-center justify-center leading-none text-gray-500 text-sm tracking-wider uppercase pt-4">
                            <TimePicker
                                className={classNames(
                                    'focus:ring-primary-light focus:border-primary-light w-32 block shadow-sm border-gray-300 rounded-md h-8 calendar text-sm text-inherit'
                                )}
                                format="hh:mm a"
                                onChange={(e) => {
                                    if (e.target.value) {
                                        p.data.dataItem.start = e.target.value
                                        setStaffDto({
                                            ...staff_dto,
                                            from_time: p.data.dataItem.start,
                                        })
                                    }
                                }}
                                defaultValue={
                                    p.data.dataItem.start as Date
                                }
                                popupSettings={{
                                    popupClass: "z-[102]"
                                }}
                            /> -
                            <TimePicker
                                className={classNames(
                                    'focus:ring-primary-light focus:border-primary-light w-32 block shadow-sm border-gray-300 rounded-md h-8 calendar text-sm text-inherit'
                                )}
                                format="hh:mm a"
                                onChange={(e) => {
                                    if (e.target.value) {
                                        p.data.dataItem.end = e.target.value
                                        setStaffDto({
                                            ...staff_dto,
                                            to_time: p.data.dataItem.end,
                                        })
                                    }
                                }}
                                defaultValue={
                                    p.data.dataItem.end as Date
                                }
                                popupSettings={{
                                    popupClass: "z-[102]"
                                }}
                            />
                        </div>
                        <div className="align-middle inline-block min-w-full">
                            <RenderAvailabilityTabs />
                        </div>
                        <div className="flex items-center">
                            <DropDownList
                                className="bg-white py-2 mt-3 block w-1/2 shadow-sm border border-gray-300 rounded-md text-sm mr-2"
                                iconClassName="feather-chevron-down"
                                data={(locations) || []}
                                value={{id : staff_dto?.location?.value || staff_dto?.location?.id, name: staff_dto?.location?.text || staff_dto?.location?.name}} //backwards compat
                                textField="name"
                                dataItemKey="id"
                                required={true}
                                valueRender={LocationValueRender}
                                onChange={(e) => {
                                    updateLocationSpace(e.target.value)
                                }}
                                popupSettings={{
                                    className: "z-[102]"
                                }}
                            />
                            <ServiceAvailSelector
                                className="z-10 py-2 mt-3 w-1/2 bg-white block shadow-sm border border-gray-300 rounded-md text-sm"
                                items={space_items || []}
                                overrideServices={selected_spaces.length != 0 ? selected_spaces : space_items}
                                title="Space"
                                updateServices={(services: any[]) => {
                                    staff_dto.location_space = services
                                }}
                                update_on_select={true}
                                icon="feather-briefcase"
                            />
                        </div>

                        <div className="flex items-center">
                            <CustomSelectServices
                                attributes={attributes}
                                setAttributes={setAttributes}
                                translations={translations}
                            />
                        </div>
                        <div className="flex gap-3 justify-end">
                            <button
                                className={classNames(
                                    'group relative flex justify-center',
                                    'py-1 px-2 border border-red-400 ',
                                    'rounded-md text-white',
                                    'focus:outline-none mt-5',
                                    !show_delete ? 'hidden' : ''
                                )}
                                onClick={() => {
                                    setShowConfirmation(true)
                                }}
                            >
                                <i className="feather-trash-2 text-xl text-red-400" />
                            </button>
                            <button
                                id="ACCOUNT_INFORMATION"
                                className={classNames(
                                    'group relative flex justify-center items-center',
                                    'py-1 px-2 border border-transparent',
                                    'rounded-md text-white',
                                    'focus:outline-none bg-red-400 mt-5',
                                )}
                                onClick={() => {
                                    setShowModal(false)
                                }}
                            >
                                <Translation content_key="cancel" />
                            </button>
                            <button
                                className={classNames(
                                    'group relative flex justify-center items-center',
                                    'py-1 px-2 border border-transparent',
                                    'rounded-md text-white',
                                    'focus:outline-none bg-primary mt-5',
                                    disabled ? "cursor-not-allowed bg-opacity-50" : ""
                                )}
                                disabled={disabled}
                                onClick={() => {
                                    handleSubmitForm()
                                }}
                            >
                                <Translation content_key="set_availability" />
                            </button>
                            {showConfirmation && (
                                <ConfirmDialogModal
                                    prompt_message={getTranslation('delete_availability_prompt', translations)}
                                    onCancel={() => {
                                        setShowConfirmation(false)
                                    }}
                                    onConfirm={() => {
                                        p.onDelete?.(p.data.dataItem.id)
                                        setShowModal(false)
                                    }}
                                />
                            )}
                        </div>
                    </div>
                </Transition.Child>
            </Transition>
        </div>
    )
}
